
import { defineComponent } from "vue";
import JobModal from "./JobModal.vue";
import LookupDataService from "@/services/LookupDataService";
import { isBoss } from "@/utils/auth";

export default defineComponent({
  name: "OfficerJobsCalendarPopup",
  components: { JobModal },
  props: {
    baseUrl: { type: String, required: true },
    officerId: { type: Number, required: true },
    officerName: { type: String, default: "" }
  },
  emits: ["close"],
  data() {
    const now = new Date();
    return {
      currentMonth: new Date(now.getFullYear(), now.getMonth(), 1),
      todayKey: "",
      jobs: [] as any[],
      nonAvailabilities: [] as any[],
      loading: false,
      message: "",
      maxJobsPerCell: 3,
      weekdayLabels: ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"],
      // Same cached lookup lists the Schedule page passes to JobModal
      lookup: LookupDataService.getData() || {},
      // Days whose "+N more" has been opened to show every job
      expandedDays: {} as Record<string, boolean>
    };
  },
  computed: {
    monthLabel(): string {
      return this.currentMonth.toLocaleDateString(undefined, { month: "long", year: "numeric" });
    },
    isPastMonth(): boolean {
      const nextMonthStart = new Date(this.currentMonth.getFullYear(), this.currentMonth.getMonth() + 1, 1);
      return this.formatDateKey(nextMonthStart) <= this.todayKey;
    },
    // The API also returns the previous month's last day (only for the overnight overlap check below)
    monthJobs(): any[] {
      const monthPrefix = this.formatDateKey(this.currentMonth).substring(0, 7);
      return this.jobs.filter((job: any) => (job.jobDate || "").startsWith(monthPrefix));
    },
    // jobId -> the other jobs it overlaps. Same rule as the API's CheckOfficerForJob: start < other end
    // and end > other start, with overnight jobs running into the next day (so back-to-back is fine).
    overlapsByJobId(): Record<number, any[]> {
      const spans = this.jobs
        .map((job: any) => {
          const start = this.toMinutes(job.startTime);
          const minutes = this.jobMinutes(job);
          if (start === null || minutes <= 0) return null;
          const [y, m, d] = (job.jobDate || "").split("T")[0].split("-").map(Number);
          const startsAt = new Date(y, m - 1, d).getTime() + start * 60000;
          return { job, startsAt, endsAt: startsAt + minutes * 60000 };
        })
        .filter(Boolean) as { job: any; startsAt: number; endsAt: number }[];

      const map: Record<number, any[]> = {};
      for (const a of spans) {
        for (const b of spans) {
          if (a !== b && a.startsAt < b.endsAt && a.endsAt > b.startsAt) {
            (map[a.job.jobId] = map[a.job.jobId] || []).push(b.job);
          }
        }
      }
      return map;
    },
    jobsByDate(): Record<string, any[]> {
      const map: Record<string, any[]> = {};
      for (const job of this.jobs) {
        const key = (job.jobDate || "").split("T")[0];
        (map[key] = map[key] || []).push(job);
      }
      return map;
    },
    // Rejected requests don't block anything, so they aren't shown; a missing status is treated as approved
    nonAvailabilityByDate(): Record<string, any> {
      const map: Record<string, any> = {};
      for (const record of this.nonAvailabilities) {
        if (record.status === "Rejected") continue;
        const key = (record.nonAvailabilityDate || "").split("T")[0];
        if (!map[key] || map[key].status === "Pending") map[key] = record;
      }
      return map;
    },
    calendarWeeks(): any[][] {
      const firstOfMonth = this.currentMonth;
      // Monday-first week, same as the non-availability calendar
      const firstWeekday = (firstOfMonth.getDay() + 6) % 7;
      const cursor = new Date(firstOfMonth);
      cursor.setDate(cursor.getDate() - firstWeekday);

      const weeks: any[][] = [];
      for (let w = 0; w < 6; w++) {
        const week: any[] = [];
        for (let d = 0; d < 7; d++) {
          const key = this.formatDateKey(cursor);
          const jobs = this.jobsByDate[key] || [];
          const nonAvailability = this.nonAvailabilityByDate[key] || null;
          const isUnavailable = !!nonAvailability && nonAvailability.status !== "Pending";
          week.push({
            key,
            day: cursor.getDate(),
            inCurrentMonth: cursor.getMonth() === firstOfMonth.getMonth(),
            isPast: key < this.todayKey,
            isToday: key === this.todayKey,
            jobs,
            nonAvailability,
            isUnavailable,
            isClash: isUnavailable && jobs.length > 0,
            hasOverlap: jobs.some((job: any) => !!this.overlapsByJobId[job.jobId]),
            isFree: !isUnavailable && jobs.length === 0,
            // Days that already have jobs are allowed too (second shifts); approved non-availability is not
            canAddJob: cursor.getMonth() === firstOfMonth.getMonth() && key >= this.todayKey && !isUnavailable
          });
          cursor.setDate(cursor.getDate() + 1);
        }
        // Drop a trailing week that belongs entirely to the next month
        if (w > 3 && !week.some(c => c.inCurrentMonth)) break;
        weeks.push(week);
      }
      return weeks;
    },
    summary(): { freeDaysLeft: number; workingDays: number; hours: string; overlapDays: number } {
      const monthCells = this.calendarWeeks.flat().filter((c: any) => c.inCurrentMonth);
      const minutes = this.monthJobs.reduce((total: number, job: any) => total + this.jobMinutes(job), 0);
      return {
        freeDaysLeft: monthCells.filter((c: any) => c.isFree && !c.isPast).length,
        workingDays: monthCells.filter((c: any) => c.jobs.length > 0).length,
        hours: (Math.round(minutes / 6) / 10).toString(),
        overlapDays: monthCells.filter((c: any) => c.hasOverlap).length
      };
    }
  },
  watch: {
    officerId: {
      immediate: true,
      handler() {
        this.fetchNonAvailabilities();
        this.fetchJobs();
      }
    },
    currentMonth() {
      this.fetchJobs();
    }
  },
  created() {
    this.todayKey = this.formatDateKey(new Date());
  },
  methods: {
    formatDateKey(date: Date): string {
      return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, "0")}-${String(date.getDate()).padStart(2, "0")}`;
    },
    toMinutes(time: string | null): number | null {
      if (!time) return null;
      const [h, m] = time.split(":").map(Number);
      return h * 60 + m;
    },
    // Overnight job (end before start) runs into the next day
    jobMinutes(job: any): number {
      const start = this.toMinutes(job.startTime);
      const end = this.toMinutes(job.endTime);
      if (start === null || end === null) return 0;
      return end < start ? end + 24 * 60 - start : end - start;
    },
    jobLabel(job: any): string {
      const start = (job.startTime || "").substring(0, 5);
      const end = (job.endTime || "").substring(0, 5);
      const overnight = (this.toMinutes(job.endTime) ?? 0) < (this.toMinutes(job.startTime) ?? 0) ? " (+1)" : "";
      const site = [job.siteName, job.postCode].filter(Boolean).join(", ");
      return `${start}–${end}${overnight} ${site}`.trim();
    },
    cellClass(cell: any) {
      if (!cell.inCurrentMonth) return { "cell-outside": true };
      return {
        "cell-clash": cell.isClash,
        "cell-unavailable": cell.isUnavailable && !cell.isClash,
        "cell-overlap": cell.hasOverlap && !cell.isClash,
        "cell-jobs": !cell.isUnavailable && !cell.hasOverlap && cell.jobs.length > 0,
        "cell-free": cell.isFree && !cell.nonAvailability,
        // Pending requests still count as free (they don't block assignment) but are shaded so they're noticed
        "cell-pending": cell.isFree && !!cell.nonAvailability,
        "cell-past": cell.isPast,
        "cell-today": cell.isToday,
        "cell-addable": cell.canAddJob
      };
    },
    cellTitle(cell: any): string {
      if (!cell.inCurrentMonth) return "";
      const lines: string[] = [];
      if (cell.nonAvailability) {
        const na = cell.nonAvailability;
        lines.push(`${na.nonAvailabilityType || "Unavailable"}${na.status === "Pending" ? " (pending approval)" : ""}${na.reason ? " - " + na.reason : ""}`);
      }
      lines.push(...cell.jobs.map((job: any) => this.jobTitle(job)));
      if (cell.isClash) lines.push("Clash: jobs on an unavailable day");
      if (cell.jobs.length) lines.push("Click a job to edit it");
      if (lines.length === 0) lines.push(cell.isPast ? "Past day" : "Free");
      if (cell.canAddJob) lines.push("Click to add a job");
      return lines.join("\n");
    },
    jobTitle(job: any): string {
      const others = this.overlapsByJobId[job.jobId] || [];
      if (!others.length) return this.jobLabel(job);
      return `${this.jobLabel(job)}\n  ⚠ Overlaps: ${others.map((o: any) => this.jobLabel(o)).join("; ")}`;
    },
    toggleExpanded(key: string) {
      this.expandedDays = { ...this.expandedDays, [key]: !this.expandedDays[key] };
    },
    // Past jobs open read-only except for Boss, the same rule as the Schedule page (ScheduleView.isJobLocked)
    editJob(job: any) {
      const locked = (job.jobDate || "").split("T")[0] < this.todayKey && !isBoss();
      (this.$refs.jobModal as any).openForEdit({ ...job, securityOfficerId: job.securityOfficerId ?? this.officerId }, locked);
    },
    onCellClick(cell: any) {
      if (!cell.canAddJob) return;
      (this.$refs.jobModal as any).openForAdd({ officerId: this.officerId, jobDate: cell.key });
    },
    previousMonth() {
      this.currentMonth = new Date(this.currentMonth.getFullYear(), this.currentMonth.getMonth() - 1, 1);
    },
    nextMonth() {
      this.currentMonth = new Date(this.currentMonth.getFullYear(), this.currentMonth.getMonth() + 1, 1);
    },
    authHeaders() {
      return {
        Authorization: `Bearer ${localStorage.getItem("token")}`,
        Accept: "application/json"
      };
    },
    async fetchJobs() {
      if (!this.officerId) return;
      const year = this.currentMonth.getFullYear();
      const month = this.currentMonth.getMonth() + 1;
      this.loading = true;
      this.message = "";
      try {
        const response = await fetch(
          `${this.baseUrl}Schedule/OfficerMonthJobs?securityOfficerId=${this.officerId}&year=${year}&month=${month}`,
          { method: "GET", headers: this.authHeaders() }
        );
        // Ignore a late response for a month the user has already moved away from
        if (year !== this.currentMonth.getFullYear() || month !== this.currentMonth.getMonth() + 1) return;
        if (response.ok) {
          this.jobs = await response.json();
        } else {
          this.jobs = [];
          this.message = "Failed to load jobs.";
        }
      } catch (error) {
        console.error("Error loading officer jobs:", error);
        this.message = "Network error while loading jobs.";
      } finally {
        this.loading = false;
      }
    },
    async fetchNonAvailabilities() {
      try {
        const response = await fetch(`${this.baseUrl}SecurityOfficers/NonAvailability/${this.officerId}`, {
          method: "GET",
          headers: this.authHeaders()
        });
        if (response.ok) {
          this.nonAvailabilities = await response.json();
        }
      } catch (error) {
        console.error("Error loading non-availability:", error);
      }
    }
  }
});
